import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IdleTimeoutDialogComponent } from 'src/app/pages/authentication/idle-timeout-dialogue/idle-timeout-dialogue.component';

@Injectable({ providedIn: 'root' })
export class IdleService {
  private idleTimeout = 24 * 60 * 60 * 1000; // inactivity duration
  private idleWarningTime = 1 * 60 * 1000; // countdown time before logout
  private lastActivityTime: number = Date.now();
  private checkIntervalId: any = null;

  private idleDialogRef: MatDialogRef<any> | null = null;
  private dialogOpen = false;

  // Performance improvements
  private lastActivityUpdate = 0;
  private readonly ACTIVITY_THROTTLE = 1000; // Update activity max once per second
  private readonly CHECK_INTERVAL = 10000; // Check every 10 seconds instead of 1 second

  // Multi-tab synchronization
  private readonly STORAGE_KEY = 'remit2any_idle_activity';
  private readonly STORAGE_UPDATE_INTERVAL = 5000; // Update shared storage every 5 seconds
  private storageUpdateTimer: any = null;

  private performanceMetrics = {
    checksPerformed: 0,
    activityEvents: 0,
    startTime: Date.now(),
  };

  private readonly STAY_LOGGED_IN_KEY = 'remit2any_idle_stay_logged_in';

  constructor(
    private dialog: MatDialog,
    private ngZone: NgZone,
  ) {}

  startWatching(): void {
    console.log('🚀 IdleService: Starting optimized idle watching');
    console.log(
      `⚙️ Settings: Check interval: ${this.CHECK_INTERVAL / 1000}s, Activity throttle: ${this.ACTIVITY_THROTTLE / 1000}s`,
    );
    this.stopWatching();

    this.lastActivityTime = Date.now();
    this.performanceMetrics.startTime = Date.now();
    this.performanceMetrics.checksPerformed = 0;
    this.performanceMetrics.activityEvents = 0;

    // Optimized event listeners - removed mousemove for performance, added throttled scroll
    const activityEvents = ['keydown', 'click', 'touchstart'];
    activityEvents.forEach((event) =>
      window.addEventListener(event, this.updateLastActivity),
    );

    // Add throttled scroll listener separately
    window.addEventListener('scroll', this.throttledScrollHandler);

    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Listen for storage changes from other tabs
    window.addEventListener('storage', this.handleStorageChange);

    this.ngZone.runOutsideAngular(() => {
      this.checkIntervalId = setInterval(() => {
        this.checkIdleTime();
      }, this.CHECK_INTERVAL); // 10 seconds instead of 1 second (90% reduction)
    });

    // Start periodic shared storage updates
    this.storageUpdateTimer = setInterval(() => {
      this.updateSharedActivity(this.lastActivityTime);
    }, this.STORAGE_UPDATE_INTERVAL);

    console.log(
      `✅ IdleService: Watching started with ${activityEvents.length + 1} optimized event listeners + multi-tab sync`,
    );
  }

  stopWatching(): void {
    console.log('🛑 IdleService: Stopping optimized idle watching');

    // Clean up all event listeners
    window.removeEventListener('keydown', this.updateLastActivity);
    window.removeEventListener('click', this.updateLastActivity);
    window.removeEventListener('touchstart', this.updateLastActivity);
    window.removeEventListener('scroll', this.throttledScrollHandler);
    window.removeEventListener('storage', this.handleStorageChange);

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );

    // Clean up storage update timer
    if (this.storageUpdateTimer) {
      clearInterval(this.storageUpdateTimer);
      this.storageUpdateTimer = null;
    }

    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }

    if (this.idleDialogRef) {
      this.idleDialogRef.close();
      this.idleDialogRef = null;
    }

    this.dialogOpen = false;

    // Clear localStorage activity timestamp to prevent stale data issues on public routes
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log(
        '🗑️ IdleService: Cleared shared activity timestamp from storage',
      );
    } catch (error) {
      console.warn(
        '⚠️ IdleService: Could not clear shared activity storage:',
        error,
      );
    }

    // Log performance metrics
    this.logPerformanceMetrics();
    console.log('✅ IdleService: Watching stopped, all resources cleaned up');
  }

  private updateLastActivity = () => {
    const now = Date.now();
    // Throttle activity updates to prevent excessive calls
    if (now - this.lastActivityUpdate >= this.ACTIVITY_THROTTLE) {
      this.lastActivityTime = now;
      this.lastActivityUpdate = now;
      this.performanceMetrics.activityEvents++;

      // Update shared storage for multi-tab synchronization
      this.updateSharedActivity(now);

      if (this.performanceMetrics.activityEvents % 50 === 0) {
        console.log(
          `📊 IdleService: ${this.performanceMetrics.activityEvents} activity events detected`,
        );
      }
    }
  };

  private updateSharedActivity(timestamp: number): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, timestamp.toString());
    } catch (error) {
      console.warn(
        '⚠️ IdleService: Could not update shared activity storage:',
        error,
      );
    }
  }

  private getSharedActivity(): number {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? parseInt(stored, 10) : 0;
    } catch (error) {
      console.warn(
        '⚠️ IdleService: Could not read shared activity storage:',
        error,
      );
      return 0;
    }
  }

  private throttledScrollHandler = () => {
    // Scroll events are heavily throttled since they fire very frequently
    const now = Date.now();
    if (now - this.lastActivityUpdate >= this.ACTIVITY_THROTTLE * 2) {
      // 2-second throttle for scroll
      this.updateLastActivity();
    }
  };

  private handleVisibilityChange = () => {
    // Safety check: Only handle visibility changes if watching is active
    if (!this.checkIntervalId) {
      console.log(
        '⚠️ IdleService: Visibility change detected but watching is stopped, ignoring',
      );
      return;
    }

    if (!document.hidden) {
      const now = Date.now();

      // Check both local and shared activity when tab becomes visible
      const sharedActivity = this.getSharedActivity();
      const localActivity = this.lastActivityTime;
      const mostRecentActivity = Math.max(sharedActivity, localActivity);

      const inactiveTime = now - mostRecentActivity;
      const inactiveMinutes = Math.floor(inactiveTime / 60000);

      console.log(
        `👁️ IdleService: Tab became visible after ${inactiveMinutes}m of inactivity`,
      );

      if (inactiveTime >= this.idleTimeout) {
        console.log(
          '⏰ IdleService: Tab was hidden too long, immediate logout required',
        );
        this.ngZone.run(() => {
          if (this.dialogOpen) {
            this.dialogRefClose(false); // close and logout
          } else {
            this.logoutImmediately();
          }
        });
      } else if (
        inactiveTime >= this.idleTimeout - this.idleWarningTime &&
        !this.dialogOpen
      ) {
        console.log(
          '⚠️ IdleService: Tab return within warning period, showing dialog',
        );
        this.ngZone.run(() => this.showDialog()); // show warning
      } else {
        console.log('✅ IdleService: Tab return within safe period');
      }
    }
  };

  private logoutFn: (() => void) | null = null;

  setLogoutCallback(fn: () => void): void {
    this.logoutFn = fn;
  }

  private logoutImmediately(): void {
    if (this.logoutFn) {
      this.logoutFn();
    }
  }

  private checkIdleTime(): void {
    // Safety check: Don't show dialog if watching was stopped
    if (!this.checkIntervalId) {
      console.log(
        '⚠️ IdleService: Check called but watching is stopped, ignoring',
      );
      return;
    }

    const now = Date.now();

    // Get the most recent activity from any tab
    const sharedActivity = this.getSharedActivity();
    const localActivity = this.lastActivityTime;
    const mostRecentActivity = Math.max(sharedActivity, localActivity);

    const inactiveTime = now - mostRecentActivity;
    const warningThreshold = this.idleTimeout - this.idleWarningTime;

    this.performanceMetrics.checksPerformed++;

    // Log periodic status (every 6 checks = 1 minute with 10s intervals)
    if (this.performanceMetrics.checksPerformed % 6 === 0) {
      const inactiveMinutes = Math.floor(inactiveTime / 60000);
      const inactiveSeconds = Math.floor((inactiveTime % 60000) / 1000);
      const source = sharedActivity > localActivity ? 'shared' : 'local';
      console.log(
        `⏱️ IdleService: Idle check #${this.performanceMetrics.checksPerformed} - inactive: ${inactiveMinutes}m ${inactiveSeconds}s (${source})`,
      );
    }

    if (
      inactiveTime >= warningThreshold &&
      inactiveTime < this.idleTimeout &&
      !this.dialogOpen
    ) {
      this.ngZone.run(() => this.showDialog());
    }

    if (inactiveTime >= this.idleTimeout && this.dialogOpen) {
      this.dialogRefClose(false); // logout if no response
    }
  }

  private handleStorageChange = (event: StorageEvent): void => {
    if (event.key === this.STAY_LOGGED_IN_KEY && event.newValue) {
      console.log('🔄 IdleService: Stay Logged In detected from another tab');
      this.resetIdleTimerFromOtherTab();
    }
    if (event.key === this.STORAGE_KEY && event.newValue) {
      const sharedTimestamp = parseInt(event.newValue, 10);
      const localTimestamp = this.lastActivityTime;

      // Only update if the shared activity is more recent
      if (sharedTimestamp > localTimestamp) {
        console.log(
          `🔄 IdleService: Activity detected in another tab at ${new Date(sharedTimestamp).toLocaleTimeString()}`,
        );
        this.lastActivityTime = sharedTimestamp;
        this.lastActivityUpdate = sharedTimestamp;
      }
    }
  };

  private logPerformanceMetrics(): void {
    const runtime = Date.now() - this.performanceMetrics.startTime;
    const runtimeMinutes = Math.round(runtime / 60000);
    const checksPerHour = Math.round(
      (this.performanceMetrics.checksPerformed * 3600000) / runtime,
    );

    console.group('📊 IdleService: Performance Summary');
    console.log(`⏱️ Runtime: ${runtimeMinutes} minutes`);
    console.log(`🔄 Total checks: ${this.performanceMetrics.checksPerformed}`);
    console.log(
      `👆 Activity events: ${this.performanceMetrics.activityEvents}`,
    );
    console.log(
      `📈 Check frequency: ${checksPerHour}/hour (vs 3600/hour with old 1s polling)`,
    );
    console.log(
      `🚀 Performance improvement: ~${Math.round((1 - checksPerHour / 3600) * 100)}% reduction in polling`,
    );
    console.groupEnd();
  }

  private showDialog(): void {
    console.log('⚠️ IdleService: Showing timeout warning dialog');
    this.dialogOpen = true;

    this.idleDialogRef = this.dialog.open(IdleTimeoutDialogComponent, {
      disableClose: true,
      width: '480px',
    });

    const warningSeconds = this.idleWarningTime / 1000;
    console.log(
      `⏰ IdleService: Auto-logout in ${warningSeconds} seconds if no user response`,
    );

    // Start logout countdown
    const autoLogoutTimer = setTimeout(() => {
      console.log('⏰ IdleService: Auto-logout timer expired');
      this.dialogRefClose(false); // logout
    }, this.idleWarningTime);

    this.idleDialogRef.afterClosed().subscribe((result) => {
      clearTimeout(autoLogoutTimer);
      this.dialogOpen = false;
      this.idleDialogRef = null;

      if (result === true) {
        this.broadcastStayLoggedIn(); // <--- Broadcast to all tabs
        console.log('✅ IdleService: User chose to stay logged in');
        this.startWatching(); // stay logged in
      } else {
        console.log('🚪 IdleService: User timeout - proceeding with logout');
      }
    });
  }

  private dialogRefClose(stayLoggedIn: boolean) {
    if (this.idleDialogRef) {
      this.idleDialogRef.close(stayLoggedIn);

      if (!stayLoggedIn && this.logoutFn) {
        this.logoutFn();
      }
    }
  }

  private broadcastStayLoggedIn(): void {
    try {
      localStorage.setItem(this.STAY_LOGGED_IN_KEY, Date.now().toString());
      console.log('🔄 IdleService: Broadcasted Stay Logged In to all tabs');
    } catch (error) {
      console.warn(
        '⚠️ IdleService: Could not broadcast Stay Logged In:',
        error,
      );
    }
  }

  private resetIdleTimerFromOtherTab(): void {
    const now = Date.now();
    this.lastActivityTime = now;
    this.lastActivityUpdate = now;
    this.updateSharedActivity(now); // <--- Add this line!
    if (this.idleDialogRef) {
      console.log(
        '🔄 IdleService: Closing warning dialog due to Stay Logged In from another tab',
      );
      this.idleDialogRef.close(true); // Simulate user clicking Stay Logged In
    }
  }

  /**
   * Public method to keep the session alive from any tab (e.g., KYB form)
   * Call this periodically to prevent auto-logout in all tabs.
   */
  public keepAlive(): void {
    const now = Date.now();
    this.lastActivityTime = now;
    this.lastActivityUpdate = now;
    this.updateSharedActivity(now);
    // Optionally log for debugging
    console.log(
      '🟢 IdleService: keepAlive() called, session refreshed at',
      new Date(now).toLocaleTimeString(),
    );
  }
}
